import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";

// DropboxReadRules - turning an index `href` into a Dropbox download, and reading what comes back.
//
// ⚠ IT HAS NO HABITAT, AND THAT IS WHY IT IS A SEPARATE FILE FROM `AppDropboxSource` -
// `DropboxIoRules`' rule and reason, unchanged. The Bridge suite cross-checks both halves of this
// wire and therefore IMPORTS this file, so a combined rules-and-transport module would drag
// `XMLHttpRequest` into a Node typecheck: it passes under tsx and fails under tsc.
//
// ==================================================================================================
// !! ⭐ THE PATH IS THE IDENTITY, WHICH IS WHY THIS FILE IS SHORT AND `AppDriveSource` IS NOT
// ==================================================================================================
//
// Reading `People/Tomo/Tomo.html` out of Drive is FOUR round trips: resolve the root folder by name,
// then each segment by name-within-parent, then fetch by id. Dropbox takes the whole path, so it is
// one request. ⭐ `DropboxSyncRun` deleted `DriveFolderWalk` for the same reason on the writing side;
// this is that saving arriving on the reading side, and it is what makes an archive openable on a
// phone connection rather than merely openable.
//
// ==================================================================================================
// !! ⛔ THE HREF ARRIVES URI-ENCODED AND DROPBOX WANTS THE REAL NAME
// ==================================================================================================
//
// `index.json` writes its hrefs for an `<a>`, so a Swedish contact is `Bj%C3%B6rn/Bj%C3%B6rn.html`.
// Handing that to Dropbox asks for a file literally called `Bj%C3%B6rn` - which does not exist, and
// answers `path/not_found`, which reads as a missing contact rather than as a double-encoding.
// ⚠ `AppDriveSource.LoadPage` decodes each segment for the same reason; this is not a new rule.
//
// ==================================================================================================
// !! ⛔ AND THE DECODED NAME IS WHY THE ARGUMENT MUST BE ESCAPED TO ASCII
// ==================================================================================================
//
// `Dropbox-API-Arg` is an HTTP HEADER, and `JSON.stringify` escapes a newline but does NOT escape
// `å`. ⛔ THERE ARE TWO FAILURE MODES AND THE QUIETER ONE IS THE DANGEROUS ONE:
//   * ABOVE LATIN-1 (an emoji, CJK) `XMLHttpRequest.setRequestHeader` throws a SyntaxError, which
//     at least stops the read where it happens.
//   * ⚠ WITHIN LATIN-1 - WHICH IS EVERY SWEDISH NAME - IT DOES NOT THROW. `ö` is U+00F6, so the
//     browser sends the single byte 0xF6 and Dropbox reads a header it expects to be UTF-8. The
//     path becomes a different string, and the answer is `path/not_found`: a contact that is
//     plainly in the folder, reported as missing, with nothing anywhere naming an encoding.
// ⚠ THE SECOND MODE IS REASONED, NOT MEASURED - the owner's own 52 contacts are all ASCII, so this
// archive cannot exercise it. ⭐ WHAT IS MEASURED (2026-09-23) is the half that matters: an ESCAPED
// non-ASCII arg is accepted by the live API and answered about the FILE (`409 path/not_found`),
// never rejected as a bad header. ⛔ On the Node side the same omission is louder - `ERR_INVALID_CHAR`
// out of `node:http`, which is how `DropboxApi` found this first.
// ⭐ THIS IS `DropboxApi.HeaderSafe`'S RULE, RESTATED ON THE BROWSER SIDE, and the Bridge suite
// asserts the two agree rather than trusting that they do.
// ⚠ WRITTEN `[^\x00-\x7f]`, NEVER A RANGE ENDING AT THE TOP CODE UNIT - that escape normalises into
// a LITERAL invisible character in the source file, in the one expression that decides this.

export class DropboxReadRules {
    public static readonly Content: string = "https://content.dropboxapi.com/2/";

    /** ⚠ MUST MATCH WHAT THE GENERATOR WRITES. The Bridge suite pins it against the folder builder;
     *  a disagreement is an archive that is plainly there and reads as absent. */
    public static readonly IndexName: string = "index.json";

    /** DOCUMENTED EXCEPTION: composes a constant.
     *  ERROR-SUPPRESSED-JUSTIFIED: pure helper, called only from a guarded method. */
    public static DownloadUrl = (): string => {
        return DropboxReadRules.Content + "files/download";
    };

    /**
     * An index `href` as a Dropbox path, or "" if it is not one this may ask for.
     *
     * ⛔ `..` IS REFUSED RATHER THAN RESOLVED, AND THE REASON SURVIVES THE APP-FOLDER SCOPE. The
     * grant cannot reach outside `Apps/WACommander`, so the worst a traversal reaches is our own
     * folder - but `index.json` is a file the SERVICE writes and this page re-reads, and a reader
     * that quietly walks wherever a document points is the wrong shape to leave lying about. It
     * costs one comparison. ⚠ An empty segment (`a//b`) is refused too: Dropbox treats it as a
     * name, so it would fail later as a puzzling `path/not_found`.
     */
    public static PathFor = (Root: string, Href: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Clean: string = nz(Href).replace(/\\/g, "/").trim();
            if (Clean.length === 0) { return ""; }
            const Out: string[] = [];
            for (const Raw of Clean.split("/")) {
                const One: string = DropboxReadRules.SegmentOf(Raw);
                if (One.length === 0) { return ""; }
                Out.push(One);
            }
            return nz(Root) + "/" + Out.join("/");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "DropboxReadRules.PathFor");
            return "";
        }
    };

    /**
     * One decoded, checked path segment, or "".
     *
     * ⚠ A MALFORMED ESCAPE IS REFUSED, NOT PASSED THROUGH. `decodeURIComponent("%zz")` THROWS, and
     * an href that cannot be decoded is not a name this build can ask Dropbox for.
     *
     * DOCUMENTED EXCEPTION: the catch returns "" rather than reporting.
     * ERROR-SUPPRESSED-JUSTIFIED: a bad href is an EXPECTED condition (law 1, fate 1) - the caller
     * turns "" into a sentence on screen. Reporting it would flip the gate and blank the page.
     */
    private static SegmentOf = (Raw: string): string => {
        try {
            if (nz(Raw).length === 0) { return ""; }
            const One: string = decodeURIComponent(nz(Raw));
            if (One === "." || One === "..") { return ""; }
            if (One.length === 0) { return ""; }
            return One;
        } catch (e) {
            return "";
        }
    };

    /**
     * The `Dropbox-API-Arg` value for a download, escaped to ASCII.
     *
     * DOCUMENTED EXCEPTION: builds a constant-shaped document from a value in hand.
     * ERROR-SUPPRESSED-JUSTIFIED: pure helper, called only from a guarded method.
     */
    public static DownloadArg = (Path: string): string => {
        return DropboxReadRules.HeaderSafe(JSON.stringify({ path: nz(Path) }));
    };

    /**
     * ⛔ SEE THE HEADER - THIS IS WHAT STOPS THE FIRST SWEDISH CONTACT BREAKING THE READER.
     *
     * ⚠ EACH CODE UNIT SEPARATELY, which is correct for an emoji in a contact name: a surrogate
     * pair becomes two `\uXXXX` escapes, which is exactly how JSON spells it.
     *
     * DOCUMENTED EXCEPTION: a pure string transform.
     * ERROR-SUPPRESSED-JUSTIFIED: cannot throw; called only from a guarded method.
     */
    public static HeaderSafe = (Json: string): string => {
        return nz(Json).replace(/[^\x00-\x7f]/g, DropboxReadRules.Escape);
    };

    /** DOCUMENTED EXCEPTION: one character in, its escape out.
     *  ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw. */
    private static Escape = (Ch: string): string => {
        return "\\u" + ("0000" + Ch.charCodeAt(0).toString(16)).slice(-4);
    };

    /**
     * Is this answer "there is no such file"?
     *
     * ⛔ DROPBOX SAYS IT IN A 409, NOT A 404 - measured on the live API, and it is the single most
     * surprising fact on this wire. A reader that tested the status alone would report a normal
     * "nothing there yet" as a hard failure, and one that tested only for 404 would never see it.
     *
     * DOCUMENTED EXCEPTION: reads two values in hand.
     * ERROR-SUPPRESSED-JUSTIFIED: pure helper, called only from a guarded method.
     */
    public static Missing = (Status: number, Text: string): boolean => {
        if (Status !== 409) { return false; }
        return nz(Text).indexOf("path/not_found") >= 0;
    };

    /**
     * What a failed read means, in a sentence.
     *
     * ⚠ 401 IS THE ONE WORTH NAMING, and it is the commonest thing that will happen here: the read
     * ticket lasts about four hours, and this is a page people leave open. ⛔ It says where the
     * archive IS rather than only that the page cannot reach it - the folder is in their Dropbox
     * and the pages open on their own, so "start again" is not the only way out.
     *
     * DOCUMENTED EXCEPTION: builds a sentence from values in hand.
     * ERROR-SUPPRESSED-JUSTIFIED: pure helper, called only from a guarded method.
     */
    public static Why = (Status: number, Text: string): string => {
        if (Status === 0) {
            return "The connection to Dropbox dropped. Nothing was changed - try again when you"
                + " have a signal.";
        }
        if (Status === 401) {
            return "This page has been open too long, so Dropbox stopped answering it. Your"
                + " WACommander folder is in your Dropbox under Apps/WACommander, and the pages"
                + " there open on their own.";
        }
        // ⚠ ONE SENTENCE COVERS A MISSING `index.json` AND A MISSING CONTACT PAGE, because the
        // cause is the same one: the folder is rebuilt on the service's schedule and this read got
        // there first. Naming "that contact" would be wrong for the index, which is the read a
        // subscriber who has just signed up actually makes.
        if (DropboxReadRules.Missing(Status, Text) === true) {
            return "That is not in your WACommander folder yet. The folder is rebuilt every few"
                + " minutes, so if your history has only just arrived, give it a moment and open"
                + " WACommander again.";
        }
        return "Dropbox could not be read (" + String(Status) + ").";
    };
}
